import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { ShoppingBag, Star, Truck, Shield, Heart, ArrowRight } from 'lucide-react';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchFeaturedProducts();
    fetchCategories();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?featured=true`);
      setFeaturedProducts(response.data.slice(0, 8));
    } catch (error) {
      console.error('Error fetching featured products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const categoryImages = {
    'Ladies Hand Bags': 'https://images.unsplash.com/photo-1608060434411-0c3fa9049e7b',
    'Sling Bags': 'https://images.unsplash.com/photo-1566150905458-1bf1fc113f0d',
    'Makeup Accessories': 'https://images.unsplash.com/photo-1569388330292-79cc1ec67270',
    'Imitation Jewelry': 'https://images.unsplash.com/photo-1611652022419-a9419f74343d',
    'School & College Bags': 'https://images.unsplash.com/photo-1614179689702-355944cd0918',
    'Stationery': 'https://images.pexels.com/photos/904350/pexels-photo-904350.jpeg',
    'Gift Souvenirs': 'https://images.unsplash.com/photo-1513885535751-8b9238bd345a',
    'Kids Toys': 'https://images.unsplash.com/photo-1608755728617-aefab37d2edd'
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="hero-gradient relative overflow-hidden">
        <div className="container mx-auto px-4 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in">
              <div className="inline-flex items-center bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full mb-6 shadow-sm">
                <Star className="w-4 h-4 text-amber-500 fill-amber-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">Trusted by Thousands</span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight" data-testid="hero-title">
                Welcome to<br />
                <span className="bg-gradient-to-r from-pink-500 via-rose-500 to-purple-500 bg-clip-text text-transparent">
                  Happy Star
                </span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Your one-stop destination for exquisite handbags, jewelry, stationery, and delightful gifts.
                Discover quality products that bring joy to every occasion.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/products">
                  <Button className="btn-primary text-white px-8 py-6 text-lg rounded-full" data-testid="shop-now-btn">
                    <ShoppingBag className="mr-2 h-5 w-5" />
                    Shop Now
                  </Button>
                </Link>
                <Link to="/products">
                  <Button variant="outline" className="px-8 py-6 text-lg rounded-full border-2 border-pink-500 text-pink-600 hover:bg-pink-50" data-testid="explore-categories-btn">
                    Explore Categories
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="floating">
                <img
                  src="https://images.unsplash.com/photo-1483985988355-763728e1935b"
                  alt="Shopping"
                  className="rounded-3xl shadow-2xl w-full"
                  data-testid="hero-image"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 glass-effect">
                <div className="flex items-center gap-3">
                  <div className="bg-pink-100 p-3 rounded-xl">
                    <Heart className="w-6 h-6 text-pink-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">5000+</p>
                    <p className="text-sm text-gray-600">Happy Customers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex items-start gap-4 p-6 rounded-2xl hover:bg-pink-50 transition-colors" data-testid="feature-shipping">
              <div className="bg-gradient-to-br from-pink-100 to-rose-100 p-4 rounded-xl">
                <Truck className="w-8 h-8 text-pink-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Free Shipping</h3>
                <p className="text-gray-600">On orders above ₹999</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 rounded-2xl hover:bg-purple-50 transition-colors" data-testid="feature-quality">
              <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-4 rounded-xl">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Quality Assured</h3>
                <p className="text-gray-600">100% authentic products</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 rounded-2xl hover:bg-amber-50 transition-colors" data-testid="feature-support">
              <div className="bg-gradient-to-br from-amber-100 to-orange-100 p-4 rounded-xl">
                <Star className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">24/7 Support</h3>
                <p className="text-gray-600">Always here to help</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-gradient-to-b from-white to-pink-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="categories-heading">Shop by Category</h2>
            <p className="text-lg text-gray-600">Explore our curated collections</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <Link to={`/products?category=${encodeURIComponent(category)}`} key={category} data-testid={`category-${index}`}>
                <div className="category-card rounded-2xl overflow-hidden shadow-lg bg-white group cursor-pointer">
                  <div className="aspect-square relative">
                    <img
                      src={categoryImages[category] || 'https://images.unsplash.com/photo-1481437156560-3205f6a55735'}
                      alt={category}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-white font-semibold text-lg">{category}</h3>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="featured-heading">Featured Products</h2>
            <p className="text-lg text-gray-600">Handpicked items just for you</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/products">
              <Button className="btn-primary text-white px-8 py-6 text-lg rounded-full" data-testid="view-all-products-btn">
                View All Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gradient-to-b from-pink-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1510284876186-b1a84b94418f"
                alt="Happy Star Shop"
                className="rounded-3xl shadow-2xl"
                data-testid="about-image"
              />
            </div>
            <div>
              <h2 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="about-heading">About Happy Star</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Located in the heart of Villukuri, Kanyakumari District, Happy Star has been serving
                the community with quality products and exceptional service. We specialize in ladies' accessories,
                fashion bags, jewelry, stationery, and delightful gifts for every occasion.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-pink-500 p-1 rounded-full mt-1">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <p className="text-gray-700">Wide range of authentic products</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-pink-500 p-1 rounded-full mt-1">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <p className="text-gray-700">Competitive prices and quality assurance</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-pink-500 p-1 rounded-full mt-1">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <p className="text-gray-700">Fast delivery across India</p>
                </div>
              </div>
              <div className="mt-8 p-6 bg-white rounded-2xl shadow-lg">
                <p className="text-gray-700 mb-2"><span className="font-semibold">Visit Us:</span> Villukuri, Kanyakumari District, Tamil Nadu</p>
                <p className="text-gray-700 mb-2"><span className="font-semibold">Call:</span> +91 908 038 8127</p>
                <p className="text-gray-700"><span className="font-semibold">Web:</span> www.happystar.in</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;